/**
 * AI Settings & Control Center Page
 * Manage, configure, and test the hybrid Groq + Ollama AI system
 */

import React, { useState, useEffect } from 'react'
import { Settings, Zap, Brain, AlertCircle, CheckCircle, Eye, EyeOff } from 'lucide-react'
import aiService from '@/services/aiService'
import AIStatusPanel from '@/components/ai/AIStatusPanel'
import AIConfigPanel from '@/components/ai/AIConfigPanel'
import AITestingPanel from '@/components/ai/AITestingPanel'
import AIMetricsPanel from '@/components/ai/AIMetricsPanel'

export default function AISettingsPage() {
  const [systemStatus, setSystemStatus] = useState(null)
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState('status') // status, config, testing, metrics
  const [config, setConfig] = useState(null)
  const [showApiKey, setShowApiKey] = useState(false)

  // Load system status on mount
  useEffect(() => {
    loadSystemStatus()
    const interval = setInterval(loadSystemStatus, 5000) // Update every 5 seconds
    return () => clearInterval(interval)
  }, [])

  const loadSystemStatus = async () => {
    try {
      const status = await aiService.getSystemStatus()
      setSystemStatus(status)
      const cfg = aiService.getConfig()
      setConfig(cfg)
      setLoading(false)
    } catch (error) {
      console.error('Failed to load system status:', error)
      setLoading(false)
    }
  }

  if (loading || !systemStatus) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900 p-6">
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <Zap className="w-12 h-12 text-blue-400 animate-pulse mx-auto mb-4" />
            <p className="text-slate-300">Loading AI System Status...</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Brain className="w-8 h-8 text-cyan-400" />
            <h1 className="text-4xl font-bold text-white">AI Control Center</h1>
          </div>
          <p className="text-slate-400">Manage and monitor your hybrid Groq + Ollama AI system</p>
        </div>

        {/* Quick Status Overview */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          {/* Groq Status */}
          <div className={`bg-gradient-to-br ${systemStatus.groq.available ? 'from-emerald-900/30 to-teal-900/30' : 'from-red-900/30 to-orange-900/30'} rounded-lg border ${systemStatus.groq.available ? 'border-emerald-500/30' : 'border-red-500/30'} p-4`}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-400 mb-1">Groq (Cloud AI)</p>
                <p className="text-sm font-mono text-slate-300">{config?.model || 'mixtral-8x7b-32768'}</p>
              </div>
              {systemStatus.groq.available ? (
                <CheckCircle className="w-6 h-6 text-emerald-400" />
              ) : (
                <AlertCircle className="w-6 h-6 text-red-400" />
              )}
            </div>
            <p className="text-xs text-slate-400 mt-2">
              Status: {systemStatus.groq.available ? 'Connected' : 'Unavailable'}
            </p>
          </div>

          {/* Ollama Status */}
          <div className={`bg-gradient-to-br ${systemStatus.ollama.available ? 'from-purple-900/30 to-blue-900/30' : 'from-red-900/30 to-orange-900/30'} rounded-lg border ${systemStatus.ollama.available ? 'border-purple-500/30' : 'border-red-500/30'} p-4`}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-400 mb-1">Ollama (Local LLM)</p>
                <p className="text-sm font-mono text-slate-300">neural-chat (configurable)</p>
              </div>
              {systemStatus.ollama.available ? (
                <CheckCircle className="w-6 h-6 text-purple-400" />
              ) : (
                <AlertCircle className="w-6 h-6 text-red-400" />
              )}
            </div>
            <p className="text-xs text-slate-400 mt-2">
              Status: {systemStatus.ollama.available ? 'Connected' : 'Not running'}
            </p>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex gap-2 mb-8 border-b border-blue-500/20 overflow-x-auto">
          {[
            { id: 'status', label: 'Status', icon: CheckCircle },
            { id: 'config', label: 'Configuration', icon: Settings },
            { id: 'testing', label: 'Testing', icon: Zap },
            { id: 'metrics', label: 'Learning Metrics', icon: Brain },
          ].map(tab => {
            const Icon = tab.icon
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-all whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-blue-400 text-blue-400'
                    : 'border-transparent text-slate-400 hover:text-slate-300'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            )
          })}
        </div>

        {/* Tab Content */}
        <div>
          {activeTab === 'status' && (
            <AIStatusPanel systemStatus={systemStatus} config={config} />
          )}
          {activeTab === 'config' && (
            <AIConfigPanel config={config} onConfigUpdate={loadSystemStatus} />
          )}
          {activeTab === 'testing' && (
            <AITestingPanel systemStatus={systemStatus} />
          )}
          {activeTab === 'metrics' && (
            <AIMetricsPanel systemStatus={systemStatus} />
          )}
        </div>
      </div>
    </div>
  )
}
